import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Loader2, User, Building2, MapPin, Phone, Ruler, GraduationCap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/services/api';
import type {
  CreatePrisonerDto,
  ProtectionLevel,
  Gender,
  MaritalStatus,
  CustodyType,
  SecurityLevel,
} from '@/types';

// Country codes for nationality selection
const COUNTRIES = [
  { code: 'DE', name: 'Deutschland' },
  { code: 'AT', name: 'Österreich' },
  { code: 'CH', name: 'Schweiz' },
  { code: 'PL', name: 'Polen' },
  { code: 'TR', name: 'Türkei' },
  { code: 'IT', name: 'Italien' },
  { code: 'FR', name: 'Frankreich' },
  { code: 'NL', name: 'Niederlande' },
  { code: 'RO', name: 'Rumänien' },
  { code: 'BG', name: 'Bulgarien' },
  { code: 'HR', name: 'Kroatien' },
  { code: 'RS', name: 'Serbien' },
  { code: 'RU', name: 'Russland' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'SY', name: 'Syrien' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'IQ', name: 'Irak' },
  { code: 'IR', name: 'Iran' },
  { code: 'OTHER', name: 'Andere' },
];

export function NewPrisonerPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [formData, setFormData] = useState<CreatePrisonerDto>({
    prisonerNumber: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    admissionDate: new Date().toISOString().split('T')[0],
    releaseDate: '',
    protectionLevel: 'NORMAL' as ProtectionLevel,
    identity: {
      middleNames: '',
      birthName: '',
      birthPlace: '',
      gender: 'UNKNOWN' as Gender,
      maritalStatus: 'UNKNOWN' as MaritalStatus,
      nationalities: ['DE'],
    },
    custody: {
      custodyType: 'PRISON' as CustodyType,
      legalStatus: undefined,
      facilityName: '',
      department: '',
      station: '',
      cellNumber: '',
      securityLevel: 'STANDARD' as SecurityLevel,
      custodyStart: new Date().toISOString().split('T')[0],
      expectedEnd: '',
      courtName: '',
      judgmentReference: '',
      sentenceDuration: '',
    },
    address: {
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: 'DE',
    },
    emergencyContact: {
      firstName: '',
      lastName: '',
      relationship: '',
      phone: '',
      email: '',
      address: '',
    },
    biometrics: {
      heightCm: undefined,
      eyeColor: undefined,
      hairColor: undefined,
      distinguishingMarks: '',
    },
    socialData: {
      education: '',
      profession: '',
      languages: [],
      religion: '',
      hasChildren: undefined,
      numberOfChildren: undefined,
      familyNotes: '',
      residenceStatus: '',
      countryOfOrigin: '',
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState<string>('personal');

  const createMutation = useMutation({
    mutationFn: (data: CreatePrisonerDto) => api.createPrisoner(data),
    onSuccess: (prisoner) => {
      queryClient.invalidateQueries({ queryKey: ['prisoners'] });
      toast({
        title: 'Gefangener aufgenommen',
        description: `${prisoner.firstName} ${prisoner.lastName} wurde erfolgreich aufgenommen.`,
      });
      navigate(`/prisoners/${prisoner.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: 'Fehler',
        description: error.message || 'Die Aufnahme konnte nicht durchgeführt werden.',
        variant: 'destructive',
      });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // prisonerNumber ist jetzt optional - wird automatisch generiert
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Vorname ist erforderlich';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Nachname ist erforderlich';
    }
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Geburtsdatum ist erforderlich';
    }
    if (!formData.admissionDate) {
      newErrors.admissionDate = 'Aufnahmedatum ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast({
        title: 'Validierungsfehler',
        description: 'Bitte füllen Sie alle Pflichtfelder aus.',
        variant: 'destructive',
      });
      return;
    }

    // Clean up the data - remove empty nested objects
    const submitData: CreatePrisonerDto = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      dateOfBirth: formData.dateOfBirth,
      admissionDate: formData.admissionDate,
      protectionLevel: formData.protectionLevel,
    };

    // Only include prisonerNumber if provided (otherwise auto-generated by backend)
    if (formData.prisonerNumber?.trim()) {
      submitData.prisonerNumber = formData.prisonerNumber.trim();
    }

    if (formData.releaseDate && formData.releaseDate.trim() !== '') {
      submitData.releaseDate = formData.releaseDate;
    }

    // Include identity if any field is set - clean up empty values
    if (formData.identity && (
      formData.identity.birthPlace ||
      formData.identity.birthName ||
      formData.identity.middleNames ||
      formData.identity.gender !== 'UNKNOWN' ||
      formData.identity.maritalStatus !== 'UNKNOWN' ||
      (formData.identity.nationalities && formData.identity.nationalities.length > 0)
    )) {
      submitData.identity = {
        ...(formData.identity.middleNames && { middleNames: formData.identity.middleNames }),
        ...(formData.identity.birthName && { birthName: formData.identity.birthName }),
        ...(formData.identity.birthPlace && { birthPlace: formData.identity.birthPlace }),
        ...(formData.identity.gender && formData.identity.gender !== 'UNKNOWN' && { gender: formData.identity.gender }),
        ...(formData.identity.maritalStatus && formData.identity.maritalStatus !== 'UNKNOWN' && { maritalStatus: formData.identity.maritalStatus }),
        ...(formData.identity.nationalities && formData.identity.nationalities.length > 0 && { nationalities: formData.identity.nationalities }),
      };
    }

    // Include custody if custodyType is set
    if (formData.custody?.custodyType) {
      // Clean up empty date fields - only include if they have a value
      submitData.custody = {
        custodyType: formData.custody.custodyType,
        ...(formData.custody.legalStatus && { legalStatus: formData.custody.legalStatus }),
        ...(formData.custody.facilityName && { facilityName: formData.custody.facilityName }),
        ...(formData.custody.department && { department: formData.custody.department }),
        ...(formData.custody.station && { station: formData.custody.station }),
        ...(formData.custody.cellNumber && { cellNumber: formData.custody.cellNumber }),
        ...(formData.custody.securityLevel && { securityLevel: formData.custody.securityLevel }),
        ...(formData.custody.custodyStart && { custodyStart: formData.custody.custodyStart }),
        ...(formData.custody.expectedEnd && { expectedEnd: formData.custody.expectedEnd }),
        ...(formData.custody.courtName && { courtName: formData.custody.courtName }),
        ...(formData.custody.judgmentReference && { judgmentReference: formData.custody.judgmentReference }),
        ...(formData.custody.sentenceDuration && { sentenceDuration: formData.custody.sentenceDuration }),
      };
    }

    // Include address if any field is set - clean up empty values
    if (formData.address && (formData.address.street || formData.address.city)) {
      submitData.address = {
        ...(formData.address.type && { type: formData.address.type }),
        ...(formData.address.street && { street: formData.address.street }),
        ...(formData.address.houseNumber && { houseNumber: formData.address.houseNumber }),
        ...(formData.address.postalCode && { postalCode: formData.address.postalCode }),
        ...(formData.address.city && { city: formData.address.city }),
        ...(formData.address.country && { country: formData.address.country }),
      };
    }

    // Include emergency contact if name is set - clean up empty values
    if (formData.emergencyContact?.firstName && formData.emergencyContact?.lastName) {
      submitData.emergencyContact = {
        firstName: formData.emergencyContact.firstName,
        lastName: formData.emergencyContact.lastName,
        ...(formData.emergencyContact.relationship && { relationship: formData.emergencyContact.relationship }),
        ...(formData.emergencyContact.phone && { phone: formData.emergencyContact.phone }),
        ...(formData.emergencyContact.email && { email: formData.emergencyContact.email }),
        ...(formData.emergencyContact.address && { address: formData.emergencyContact.address }),
      };
    }

    // Include biometrics if any field is set - clean up empty values
    if (formData.biometrics && (
      formData.biometrics.heightCm ||
      formData.biometrics.eyeColor ||
      formData.biometrics.hairColor ||
      formData.biometrics.distinguishingMarks
    )) {
      submitData.biometrics = {
        ...(formData.biometrics.heightCm && { heightCm: formData.biometrics.heightCm }),
        ...(formData.biometrics.eyeColor && { eyeColor: formData.biometrics.eyeColor }),
        ...(formData.biometrics.hairColor && { hairColor: formData.biometrics.hairColor }),
        ...(formData.biometrics.distinguishingMarks && { distinguishingMarks: formData.biometrics.distinguishingMarks }),
      };
    }

    // Include social data if any field is set
    if (formData.socialData && (
      formData.socialData.education ||
      formData.socialData.profession ||
      (formData.socialData.languages && formData.socialData.languages.length > 0) ||
      formData.socialData.religion ||
      formData.socialData.hasChildren !== undefined ||
      formData.socialData.numberOfChildren !== undefined ||
      formData.socialData.familyNotes ||
      formData.socialData.residenceStatus ||
      formData.socialData.countryOfOrigin
    )) {
      submitData.socialData = {
        ...(formData.socialData.education && { education: formData.socialData.education }),
        ...(formData.socialData.profession && { profession: formData.socialData.profession }),
        ...(formData.socialData.languages &&
          formData.socialData.languages.length > 0 && { languages: formData.socialData.languages }),
        ...(formData.socialData.religion && { religion: formData.socialData.religion }),
        ...(formData.socialData.hasChildren !== undefined && { hasChildren: formData.socialData.hasChildren }),
        ...(formData.socialData.numberOfChildren !== undefined && { numberOfChildren: formData.socialData.numberOfChildren }),
        ...(formData.socialData.familyNotes && { familyNotes: formData.socialData.familyNotes }),
        ...(formData.socialData.residenceStatus && { residenceStatus: formData.socialData.residenceStatus }),
        ...(formData.socialData.countryOfOrigin && {
          countryOfOrigin: formData.socialData.countryOfOrigin.toUpperCase(),
        }),
      };
    }

    createMutation.mutate(submitData);
  };

  const updateField = (path: string, value: unknown) => {
    setFormData((prev) => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current: Record<string, unknown> = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (current[keys[i]] === undefined) {
          current[keys[i]] = {};
        }
        current[keys[i]] = { ...(current[keys[i]] as Record<string, unknown>) };
        current = current[keys[i]] as Record<string, unknown>;
      }
      current[keys[keys.length - 1]] = value;

      return newData as CreatePrisonerDto;
    });

    // Clear error when user starts typing
    if (errors[path]) {
      setErrors((prev) => ({ ...prev, [path]: '' }));
    }
  };

  const sections = [
    { id: 'personal', label: 'Persönliche Daten', icon: User },
    { id: 'custody', label: 'Vollzugsdaten', icon: Building2 },
    { id: 'address', label: 'Adresse', icon: MapPin },
    { id: 'contact', label: 'Notfallkontakt', icon: Phone },
    { id: 'biometrics', label: 'Körperliche Merkmale', icon: Ruler },
    { id: 'social', label: 'Soziales', icon: GraduationCap },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/prisoners')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Neue Aufnahme</h1>
          <p className="text-muted-foreground">
            Einen neuen Gefangenen vollständig in das System aufnehmen
          </p>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex gap-2 flex-wrap">
        {sections.map((section) => (
          <Button
            key={section.id}
            variant={activeSection === section.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveSection(section.id)}
            className="gap-2"
          >
            <section.icon className="h-4 w-4" />
            {section.label}
          </Button>
        ))}
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Section: Persönliche Daten */}
          {activeSection === 'personal' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Grunddaten</CardTitle>
                  <CardDescription>
                    Pflichtfelder für die Aufnahme
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="prisonerNumber">Gefangenennummer</Label>
                    <Input
                      id="prisonerNumber"
                      placeholder="Wird automatisch generiert (z.B. GEF-2026-00001)"
                      value={formData.prisonerNumber}
                      onChange={(e) => updateField('prisonerNumber', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Optional - wird automatisch generiert wenn leer gelassen
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Vorname *</Label>
                      <Input
                        id="firstName"
                        placeholder="Vorname"
                        value={formData.firstName}
                        onChange={(e) => updateField('firstName', e.target.value)}
                        className={errors.firstName ? 'border-red-500' : ''}
                      />
                      {errors.firstName && (
                        <p className="text-sm text-red-500">{errors.firstName}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nachname *</Label>
                      <Input
                        id="lastName"
                        placeholder="Nachname"
                        value={formData.lastName}
                        onChange={(e) => updateField('lastName', e.target.value)}
                        className={errors.lastName ? 'border-red-500' : ''}
                      />
                      {errors.lastName && (
                        <p className="text-sm text-red-500">{errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="middleNames">Weitere Vornamen</Label>
                    <Input
                      id="middleNames"
                      placeholder="Weitere Vornamen"
                      value={formData.identity?.middleNames || ''}
                      onChange={(e) => updateField('identity.middleNames', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="birthName">Geburtsname</Label>
                    <Input
                      id="birthName"
                      placeholder="Falls abweichend vom Nachnamen"
                      value={formData.identity?.birthName || ''}
                      onChange={(e) => updateField('identity.birthName', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="dateOfBirth">Geburtsdatum *</Label>
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={(e) => updateField('dateOfBirth', e.target.value)}
                        className={errors.dateOfBirth ? 'border-red-500' : ''}
                      />
                      {errors.dateOfBirth && (
                        <p className="text-sm text-red-500">{errors.dateOfBirth}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="birthPlace">Geburtsort</Label>
                      <Input
                        id="birthPlace"
                        placeholder="z.B. Berlin"
                        value={formData.identity?.birthPlace || ''}
                        onChange={(e) => updateField('identity.birthPlace', e.target.value)}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Erweiterte Personendaten</CardTitle>
                  <CardDescription>
                    Zusätzliche Informationen zur Person
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="gender">Geschlecht</Label>
                    <Select
                      value={formData.identity?.gender || 'UNKNOWN'}
                      onValueChange={(value) => updateField('identity.gender', value)}
                    >
                      <SelectTrigger id="gender">
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNKNOWN">Unbekannt</SelectItem>
                        <SelectItem value="MALE">Männlich</SelectItem>
                        <SelectItem value="FEMALE">Weiblich</SelectItem>
                        <SelectItem value="DIVERSE">Divers</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="maritalStatus">Familienstand</Label>
                    <Select
                      value={formData.identity?.maritalStatus || 'UNKNOWN'}
                      onValueChange={(value) => updateField('identity.maritalStatus', value)}
                    >
                      <SelectTrigger id="maritalStatus">
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNKNOWN">Unbekannt</SelectItem>
                        <SelectItem value="SINGLE">Ledig</SelectItem>
                        <SelectItem value="MARRIED">Verheiratet</SelectItem>
                        <SelectItem value="DIVORCED">Geschieden</SelectItem>
                        <SelectItem value="WIDOWED">Verwitwet</SelectItem>
                        <SelectItem value="REGISTERED_PARTNERSHIP">Eingetragene Partnerschaft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nationality">Staatsangehörigkeit</Label>
                    <Select
                      value={formData.identity?.nationalities?.[0] || 'DE'}
                      onValueChange={(value) => updateField('identity.nationalities', [value])}
                    >
                      <SelectTrigger id="nationality">
                        <SelectValue placeholder="Land wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="protectionLevel">Schutzstufe</Label>
                    <Select
                      value={formData.protectionLevel}
                      onValueChange={(value) => updateField('protectionLevel', value)}
                    >
                      <SelectTrigger id="protectionLevel">
                        <SelectValue placeholder="Schutzstufe wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NORMAL">Normal</SelectItem>
                        <SelectItem value="CONFIDENTIAL">Vertraulich</SelectItem>
                        <SelectItem value="STRICTLY_CONFIDENTIAL">Streng Vertraulich</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Bestimmt die Zugriffsrechte auf die Akte
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Section: Vollzugsdaten */}
          {activeSection === 'custody' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Aufnahme & Haft</CardTitle>
                  <CardDescription>
                    Informationen zur Aufnahme und zum Vollzug
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="admissionDate">Aufnahmedatum *</Label>
                      <Input
                        id="admissionDate"
                        type="date"
                        value={formData.admissionDate}
                        onChange={(e) => updateField('admissionDate', e.target.value)}
                        className={errors.admissionDate ? 'border-red-500' : ''}
                      />
                      {errors.admissionDate && (
                        <p className="text-sm text-red-500">{errors.admissionDate}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="releaseDate">Voraussichtliche Entlassung</Label>
                      <Input
                        id="releaseDate"
                        type="date"
                        value={formData.releaseDate || ''}
                        onChange={(e) => updateField('releaseDate', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="custodyType">Vollzugsart</Label>
                    <Select
                      value={formData.custody?.custodyType || 'PRISON'}
                      onValueChange={(value) => updateField('custody.custodyType', value)}
                    >
                      <SelectTrigger id="custodyType">
                        <SelectValue placeholder="Vollzugsart wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PRETRIAL">Untersuchungshaft</SelectItem>
                        <SelectItem value="PRISON">Strafhaft</SelectItem>
                        <SelectItem value="SUBSTITUTE">Ersatzfreiheitsstrafe</SelectItem>
                        <SelectItem value="PREVENTIVE">Maßregelvollzug</SelectItem>
                        <SelectItem value="JUVENILE">Jugendstrafvollzug</SelectItem>
                        <SelectItem value="CIVIL">Zivilhaft</SelectItem>
                        <SelectItem value="OTHER">Sonstige</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="legalStatus">Rechtsstatus</Label>
                    <Select
                      value={formData.custody?.legalStatus || 'NONE'}
                      onValueChange={(value) => updateField('custody.legalStatus', value === 'NONE' ? undefined : value)}
                    >
                      <SelectTrigger id="legalStatus">
                        <SelectValue placeholder="Rechtsstatus wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">Nicht angegeben</SelectItem>
                        <SelectItem value="PENDING">Rechtsmittel anhängig</SelectItem>
                        <SelectItem value="FINAL">Rechtskräftig</SelectItem>
                        <SelectItem value="PARTIALLY_FINAL">Teilweise rechtskräftig</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="securityLevel">Sicherheitsstufe</Label>
                    <Select
                      value={formData.custody?.securityLevel || 'STANDARD'}
                      onValueChange={(value) => updateField('custody.securityLevel', value)}
                    >
                      <SelectTrigger id="securityLevel">
                        <SelectValue placeholder="Sicherheitsstufe wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="STANDARD">Standard</SelectItem>
                        <SelectItem value="ELEVATED">Erhöht</SelectItem>
                        <SelectItem value="HIGH">Hoch</SelectItem>
                        <SelectItem value="MAXIMUM">Maximum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Unterbringung & Rechtliches</CardTitle>
                  <CardDescription>
                    Aktuelle Unterbringung und Urteilsdaten
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="facilityName">Anstalt</Label>
                    <Input
                      id="facilityName"
                      placeholder="z.B. JVA Beispielstadt"
                      value={formData.custody?.facilityName || ''}
                      onChange={(e) => updateField('custody.facilityName', e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="department">Abteilung</Label>
                      <Input
                        id="department"
                        placeholder="z.B. Abteilung A"
                        value={formData.custody?.department || ''}
                        onChange={(e) => updateField('custody.department', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="station">Station</Label>
                      <Input
                        id="station"
                        placeholder="z.B. Station 3"
                        value={formData.custody?.station || ''}
                        onChange={(e) => updateField('custody.station', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cellNumber">Haftraum</Label>
                      <Input
                        id="cellNumber"
                        placeholder="z.B. A-301"
                        value={formData.custody?.cellNumber || ''}
                        onChange={(e) => updateField('custody.cellNumber', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="courtName">Gericht</Label>
                    <Input
                      id="courtName"
                      placeholder="z.B. Landgericht Beispielstadt"
                      value={formData.custody?.courtName || ''}
                      onChange={(e) => updateField('custody.courtName', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="judgmentReference">Aktenzeichen</Label>
                    <Input
                      id="judgmentReference"
                      placeholder="z.B. 10 KLs 123/23"
                      value={formData.custody?.judgmentReference || ''}
                      onChange={(e) => updateField('custody.judgmentReference', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sentenceDuration">Strafmaß</Label>
                    <Input
                      id="sentenceDuration"
                      placeholder="z.B. 3 Jahre 6 Monate"
                      value={formData.custody?.sentenceDuration || ''}
                      onChange={(e) => updateField('custody.sentenceDuration', e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* Section: Adresse */}
          {activeSection === 'address' && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Letzte bekannte Adresse</CardTitle>
                <CardDescription>
                  Wohnanschrift vor der Inhaftierung (optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="street">Straße</Label>
                    <Input
                      id="street"
                      placeholder="Straßenname"
                      value={formData.address?.street || ''}
                      onChange={(e) => updateField('address.street', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="houseNumber">Hausnummer</Label>
                    <Input
                      id="houseNumber"
                      placeholder="Nr."
                      value={formData.address?.houseNumber || ''}
                      onChange={(e) => updateField('address.houseNumber', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="postalCode">PLZ</Label>
                    <Input
                      id="postalCode"
                      placeholder="12345"
                      value={formData.address?.postalCode || ''}
                      onChange={(e) => updateField('address.postalCode', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="city">Ort</Label>
                    <Input
                      id="city"
                      placeholder="Stadt"
                      value={formData.address?.city || ''}
                      onChange={(e) => updateField('address.city', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Land</Label>
                  <Select
                    value={formData.address?.country || 'DE'}
                    onValueChange={(value) => updateField('address.country', value)}
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder="Land wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Section: Notfallkontakt */}
          {activeSection === 'contact' && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Notfallkontakt</CardTitle>
                <CardDescription>
                  Kontaktperson für Notfälle (optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contactFirstName">Vorname</Label>
                    <Input
                      id="contactFirstName"
                      placeholder="Vorname der Kontaktperson"
                      value={formData.emergencyContact?.firstName || ''}
                      onChange={(e) => updateField('emergencyContact.firstName', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactLastName">Nachname</Label>
                    <Input
                      id="contactLastName"
                      placeholder="Nachname der Kontaktperson"
                      value={formData.emergencyContact?.lastName || ''}
                      onChange={(e) => updateField('emergencyContact.lastName', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="relationship">Beziehung</Label>
                    <Input
                      id="relationship"
                      placeholder="z.B. Ehefrau, Bruder, Anwalt"
                      value={formData.emergencyContact?.relationship || ''}
                      onChange={(e) => updateField('emergencyContact.relationship', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactPhone">Telefon</Label>
                    <Input
                      id="contactPhone"
                      type="tel"
                      placeholder="+49 123 456789"
                      value={formData.emergencyContact?.phone || ''}
                      onChange={(e) => updateField('emergencyContact.phone', e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactEmail">E-Mail</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    placeholder="email@beispiel.de"
                    value={formData.emergencyContact?.email || ''}
                    onChange={(e) => updateField('emergencyContact.email', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contactAddress">Adresse</Label>
                  <Textarea
                    id="contactAddress"
                    placeholder="Vollständige Adresse der Kontaktperson"
                    value={formData.emergencyContact?.address || ''}
                    onChange={(e) => updateField('emergencyContact.address', e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Section: Körperliche Merkmale */}
          {activeSection === 'biometrics' && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Körperliche Merkmale
                  <span className="text-xs font-normal bg-amber-100 text-amber-800 px-2 py-1 rounded">
                    Geschützte Daten
                  </span>
                </CardTitle>
                <CardDescription>
                  Diese Daten werden mit erhöhter Schutzstufe gespeichert (optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="heightCm">Körpergröße (cm)</Label>
                    <Input
                      id="heightCm"
                      type="number"
                      placeholder="180"
                      min={50}
                      max={250}
                      value={formData.biometrics?.heightCm || ''}
                      onChange={(e) =>
                        updateField('biometrics.heightCm', e.target.value ? parseInt(e.target.value) : undefined)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="eyeColor">Augenfarbe</Label>
                    <Select
                      value={formData.biometrics?.eyeColor || 'NONE'}
                      onValueChange={(value) => updateField('biometrics.eyeColor', value === 'NONE' ? undefined : value)}
                    >
                      <SelectTrigger id="eyeColor">
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">Nicht angegeben</SelectItem>
                        <SelectItem value="BLUE">Blau</SelectItem>
                        <SelectItem value="GREEN">Grün</SelectItem>
                        <SelectItem value="BROWN">Braun</SelectItem>
                        <SelectItem value="HAZEL">Haselnuss</SelectItem>
                        <SelectItem value="GRAY">Grau</SelectItem>
                        <SelectItem value="BLACK">Schwarz</SelectItem>
                        <SelectItem value="OTHER">Andere</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="hairColor">Haarfarbe</Label>
                    <Select
                      value={formData.biometrics?.hairColor || 'NONE'}
                      onValueChange={(value) => updateField('biometrics.hairColor', value === 'NONE' ? undefined : value)}
                    >
                      <SelectTrigger id="hairColor">
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">Nicht angegeben</SelectItem>
                        <SelectItem value="BLONDE">Blond</SelectItem>
                        <SelectItem value="BROWN">Braun</SelectItem>
                        <SelectItem value="BLACK">Schwarz</SelectItem>
                        <SelectItem value="RED">Rot</SelectItem>
                        <SelectItem value="GRAY">Grau</SelectItem>
                        <SelectItem value="WHITE">Weiß</SelectItem>
                        <SelectItem value="BALD">Glatze</SelectItem>
                        <SelectItem value="OTHER">Andere</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="distinguishingMarks">Besondere Kennzeichen</Label>
                  <Textarea
                    id="distinguishingMarks"
                    placeholder="Narben, Tätowierungen, Muttermale, etc."
                    value={formData.biometrics?.distinguishingMarks || ''}
                    onChange={(e) => updateField('biometrics.distinguishingMarks', e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Section: Soziales */}
          {activeSection === 'social' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Bildung & Beruf</CardTitle>
                  <CardDescription>
                    Soziale Grunddaten des Gefangenen
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="education">Schulbildung</Label>
                    <Input
                      id="education"
                      placeholder="z. B. Hauptschulabschluss"
                      value={formData.socialData?.education || ''}
                      onChange={(e) => updateField('socialData.education', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profession">Beruf/Ausbildung</Label>
                    <Input
                      id="profession"
                      placeholder="z. B. Anlagenmechaniker"
                      value={formData.socialData?.profession || ''}
                      onChange={(e) => updateField('socialData.profession', e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="languages">Sprachkenntnisse</Label>
                    <Input
                      id="languages"
                      placeholder="z. B. Deutsch, Englisch, Arabisch"
                      value={(formData.socialData?.languages || []).join(', ')}
                      onChange={(e) =>
                        updateField(
                          'socialData.languages',
                          e.target.value
                            .split(',')
                            .map((entry) => entry.trim())
                            .filter(Boolean)
                        )
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Familie & Aufenthaltsstatus</CardTitle>
                  <CardDescription>
                    Ergänzende soziale Informationen (optional)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="hasChildren">Kinder</Label>
                    <Select
                      value={
                        formData.socialData?.hasChildren === undefined
                          ? 'UNKNOWN'
                          : formData.socialData.hasChildren
                            ? 'YES'
                            : 'NO'
                      }
                      onValueChange={(value) =>
                        updateField(
                          'socialData.hasChildren',
                          value === 'UNKNOWN' ? undefined : value === 'YES'
                        )
                      }
                    >
                      <SelectTrigger id="hasChildren">
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="UNKNOWN">Nicht angegeben</SelectItem>
                        <SelectItem value="YES">Ja</SelectItem>
                        <SelectItem value="NO">Nein</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="numberOfChildren">Anzahl Kinder</Label>
                    <Input
                      id="numberOfChildren"
                      type="number"
                      min={0}
                      max={20}
                      value={formData.socialData?.numberOfChildren ?? ''}
                      onChange={(e) =>
                        updateField(
                          'socialData.numberOfChildren',
                          e.target.value ? parseInt(e.target.value, 10) : undefined
                        )
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="residenceStatus">Aufenthaltsstatus</Label>
                    <Input
                      id="residenceStatus"
                      placeholder="z. B. Aufenthaltserlaubnis"
                      value={formData.socialData?.residenceStatus || ''}
                      onChange={(e) => updateField('socialData.residenceStatus', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="countryOfOrigin">Herkunftsland (ISO Code)</Label>
                    <Input
                      id="countryOfOrigin"
                      placeholder="z. B. DE"
                      value={formData.socialData?.countryOfOrigin || ''}
                      onChange={(e) =>
                        updateField('socialData.countryOfOrigin', e.target.value.toUpperCase())
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="religion">Religion (sensibel)</Label>
                    <Input
                      id="religion"
                      placeholder="optional"
                      value={formData.socialData?.religion || ''}
                      onChange={(e) => updateField('socialData.religion', e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="familyNotes">Familienbezogene Hinweise</Label>
                    <Textarea
                      id="familyNotes"
                      placeholder="optionale Anmerkungen"
                      value={formData.socialData?.familyNotes || ''}
                      onChange={(e) => updateField('socialData.familyNotes', e.target.value)}
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/prisoners')}
            disabled={createMutation.isPending}
          >
            Abbrechen
          </Button>
          
          <div className="flex gap-4">
            {/* Navigation between sections */}
            {sections.findIndex((s) => s.id === activeSection) > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const currentIndex = sections.findIndex((s) => s.id === activeSection);
                  setActiveSection(sections[currentIndex - 1].id);
                }}
              >
                Zurück
              </Button>
            )}
            
            {sections.findIndex((s) => s.id === activeSection) < sections.length - 1 ? (
              <Button
                type="button"
                onClick={() => {
                  const currentIndex = sections.findIndex((s) => s.id === activeSection);
                  setActiveSection(sections[currentIndex + 1].id);
                }}
              >
                Weiter
              </Button>
            ) : (
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird gespeichert...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Aufnahme speichern
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
